import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/lib/api'

const formSchema = z.object({
  orgName: z.string().min(2, { message: 'Organization name is required' }),
  industry: z.string().min(1, { message: 'Please select an industry' }),
  companySize: z.string().min(1, { message: 'Please select company size' }),
  timezone: z.string().min(1, { message: 'Please select a timezone' }),
})

export function OrganizationSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, loading: authLoading, updateUser } = useAuth()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgName: '',
      industry: '',
      companySize: '',
      timezone: '',
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false)
  const [hasTriedStaffCompletion, setHasTriedStaffCompletion] = useState(false)

  useEffect(() => {
    if (!authLoading && user) {
      const onboardingCompleted = (user as unknown as { onboardingCompleted?: boolean }).onboardingCompleted
      const role = (user.role || user.organizationRole)?.toUpperCase?.()

      if (onboardingCompleted) {
        navigate('/dashboard', { replace: true })
        return
      }

      if (role === 'STAFF' && !isCompletingOnboarding && !hasTriedStaffCompletion) {
        setHasTriedStaffCompletion(true)
        setIsCompletingOnboarding(true)
        completeOnboardingForStaff()
      }
    }
  }, [user, authLoading, navigate, isCompletingOnboarding, hasTriedStaffCompletion])

  const completeOnboardingForStaff = async () => {
    try {
      const response = await api.post<{ success: boolean; data?: { user?: { onboardingCompleted?: boolean } } }>(
        '/onboarding/complete',
        {},
      )

      if (response?.data?.user?.onboardingCompleted ?? true) {
        updateUser({ onboardingCompleted: true })
      }

      await queryClient.invalidateQueries({ queryKey: ['auth'] })
      await queryClient.refetchQueries({ queryKey: ['auth', 'user'], type: 'active' })
      await new Promise((resolve) => setTimeout(resolve, 300))
      navigate('/dashboard', { replace: true })
    } catch (err) {
      console.error('Failed to complete onboarding for staff', err)
      navigate('/dashboard', { replace: true })
    }
  }

  if (
    authLoading ||
    isCompletingOnboarding ||
    (user && ((user as unknown as { onboardingCompleted?: boolean }).onboardingCompleted || user.role?.toLowerCase?.() === 'staff'))
  ) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true)
    try {
      const response = await api.post<{ success: boolean; data?: { user?: { onboardingCompleted?: boolean } } }>(
        '/onboarding/complete',
        values,
      )

      if (response?.data?.user?.onboardingCompleted ?? true) {
        updateUser({ onboardingCompleted: true })
      }

      await queryClient.invalidateQueries({ queryKey: ['auth', 'user'] })
      await queryClient.refetchQueries({ queryKey: ['auth', 'user'], type: 'active' })
      await new Promise((resolve) => setTimeout(resolve, 500))
      navigate('/dashboard', { replace: true })
    } catch (err) {
      console.error('Error saving organization settings', err)
      alert(`Error: ${(err as Error).message}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card border border-border shadow-clean rounded-3xl overflow-hidden w-full max-w-2xl p-12">
        <div className="text-center mb-10">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground font-bold text-xl mb-6 shadow-clean">
            1
          </div>
          <h2 className="text-4xl font-bold text-foreground mb-3 tracking-tight">Set up your organization</h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Tell us about your company and team structure to get started
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="orgName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">Organization Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your company name"
                      className="h-14 px-4 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground mb-2 block">Industry</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-14 rounded-xl border border-input">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border border-border">
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companySize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground mb-2 block">Company Size</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-14 rounded-xl border border-input">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border border-border">
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-50">11-50</SelectItem>
                        <SelectItem value="51-200">51-200</SelectItem>
                        <SelectItem value="201-500">201-500</SelectItem>
                        <SelectItem value="501-1000">501-1000</SelectItem>
                        <SelectItem value="1000+">1000+</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">Primary Timezone</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-14 rounded-xl border border-input">
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl border border-border max-h-64">
                      <SelectItem value="UTC-8">Pacific Time (UTC-8)</SelectItem>
                      <SelectItem value="UTC-5">Eastern Time (UTC-5)</SelectItem>
                      <SelectItem value="UTC+0">GMT (UTC+0)</SelectItem>
                      <SelectItem value="UTC+1">Central European Time (UTC+1)</SelectItem>
                      <SelectItem value="UTC+5:30">India Standard Time (UTC+5:30)</SelectItem>
                      <SelectItem value="UTC+8">China Standard Time (UTC+8)</SelectItem>
                      <SelectItem value="UTC+10">Australian Eastern Time (UTC+10)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Saving your organization...' : 'Continue'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}

export default OrganizationSetupPage
